import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FullWidthPageContent } from 'layouts/SingleColumnLayout';
import TextLink from 'components/TextLink';
import path from 'containers/paths/AddressesContainerPath';
import Address from 'components/Address';
import RadioButton from 'components/RadioButton';
import Button from 'components/Button';
import Checkbox, { CheckboxesArea } from 'components/Checkbox';
import {
  MAKE_THIS_MY_DELIVERY_MULTIPLE_SUB_TEXT,
  MAKE_THIS_MY_DELIVERY_SINGLE_SUB_TEXT,
  MAKE_THIS_MY_PRIMARY_ADDRESS_TEXT,
  MAKE_THIS_MY_PRIMARY_ADDRESS_TITLE,
  MAKE_THIS_MY_DELIVERY_TITLE,
} from 'helpers/copyHelper';

const StyledAddressSetView = styled.div`
  .contentWrapper {
    > div {
      margin-bottom: ${({ theme }) => theme.spacing * 3}px;
    }
  }

  .radioChildrenWrapper,
  .checkboxChildrenWrapper {
    margin-left: ${({ theme }) => theme.spacing}px;
  }

  .addressList {
    list-style: none;
    margin: ${({ theme }) => theme.spacing * 2}px 0;
    padding: 0;

    .Address {
      margin-bottom: 0;
      li:first-child{
        font-family: ${({ theme }) => theme.typography.h3.fontFamily};
        font-weight: ${({ theme }) => theme.typography.h3.fontWeight};
        font-size: ${({ theme }) => theme.typography.h3.fontSize};
      }
    }

    & > li {
      background-color: ${({ theme }) => theme.palette.colors.white};
      margin-bottom: ${({ theme }) => theme.spacing * 2}px;
    }
    label {
      padding: ${({ theme }) => theme.spacing * 3}px
        ${({ theme }) => theme.spacing * 2}px;
    }

  }

  .defaultAddressChkBox {
    h4,
    .actionMessage{
      color:${({ theme }) => theme.palette.text.main};
    }
  }

  ${CheckboxesArea} {
    margin-bottom: ${({ theme }) => theme.spacing * 2}px;
  }
`;

const AddressSetView = ({
  className,
  addresses,
  onSubmit,
  currentAddressId,
  enableApplyToAll,
  forceApplyToAll,
  defaultApplyToAll,
  enablePrimary,
  forcePrimary,
  defaultPrimary,
  multipleSubs,
}) => {
  const classes = ['AddressSetView', className];
  const defaultAddress = addresses.find((a) => a.id === currentAddressId);
  const [selectedAddress, setSelectedAddress] = useState(defaultAddress);
  const [applyToAll, setApplyToAll] = useState(defaultApplyToAll);
  const [makePrimary, setMakePrimary] = useState(defaultPrimary);

  useEffect(() => {
    setApplyToAll(applyToAll || forceApplyToAll);
    setMakePrimary(makePrimary || forcePrimary);
  }, [forceApplyToAll, forcePrimary, applyToAll, makePrimary]);

  return (
    <StyledAddressSetView className={classes.filter((c) => !!c).join(' ')}>
      <h1>Change delivery address</h1>
      <TextLink
        rightChevron
        props={{ to: path({}, 'add') }}
        dataLayer={{ action: 'Change Delivery Address', label: 'Add new address' }}
      >
        Add new address
      </TextLink>
      <FullWidthPageContent>
        <ul className="addressList">
          {addresses.map((address) => (
            <li key={address.id}>
              <RadioButton
                name="address"
                value="address.id"
                disabled={address.id === (defaultAddress && defaultAddress.id)}
                checked={address.id === (selectedAddress && selectedAddress.id)}
                onChange={() => setSelectedAddress(address)}
              >
                <Address address={address} />
              </RadioButton>
            </li>
          ))}
        </ul>
      </FullWidthPageContent>
      <CheckboxesArea>
        {enableApplyToAll && (
          <Checkbox
            onChange={(e) => setApplyToAll(e.target.checked)}
            checked={applyToAll}
            readOnly={forceApplyToAll}
            className="defaultAddressChkBox"
            dataLayer={{ action: 'Make This My Delivery Address Click' }}
          >
            <h4>{MAKE_THIS_MY_DELIVERY_TITLE()}</h4>
            <p className="actionMessage">
              {multipleSubs
                ? MAKE_THIS_MY_DELIVERY_MULTIPLE_SUB_TEXT()
                : MAKE_THIS_MY_DELIVERY_SINGLE_SUB_TEXT()}
            </p>
          </Checkbox>
        )}
        {enablePrimary && (
          <Checkbox
            onChange={(e) => setMakePrimary(e.target.checked)}
            checked={makePrimary}
            readOnly={forcePrimary}
            dataLayer={{ action: 'Make This My Primary Address Click' }}
          >
            <h4>{MAKE_THIS_MY_PRIMARY_ADDRESS_TITLE()}</h4>
            <p className="actionMessage">
              {MAKE_THIS_MY_PRIMARY_ADDRESS_TEXT()}
            </p>
          </Checkbox>
        )}
      </CheckboxesArea>

      <Button
        disabled={selectedAddress && selectedAddress.id === defaultAddress.id}
        onClick={() => onSubmit(selectedAddress, makePrimary, applyToAll)}
        dataLayer={{ action: 'Change Address Link Click', label: 'Change Address Confirm' }}
        fullWidth
      >
        Change delivery address
      </Button>
    </StyledAddressSetView>
  );
};

AddressSetView.displayName = 'AddressSetView';

AddressSetView.propTypes = {
  className: PropTypes.string,
  addresses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentAddressId: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  enableApplyToAll: PropTypes.bool,
  forceApplyToAll: PropTypes.bool,
  defaultApplyToAll: PropTypes.bool,
  enablePrimary: PropTypes.bool,
  forcePrimary: PropTypes.bool,
  defaultPrimary: PropTypes.bool,
  multipleSubs: PropTypes.bool,
};

AddressSetView.defaultProps = {
  className: null,
  enableApplyToAll: false,
  currentAddressId: null,
  forceApplyToAll: false,
  defaultApplyToAll: false,
  enablePrimary: false,
  forcePrimary: false,
  defaultPrimary: false,
  multipleSubs: false,
};

export default AddressSetView;
